/**
 * WorkflowBar — the audit lifecycle as a stepper of real stages plus the one
 * legal next move. Mirrors the Privacy War Room WorkflowBar pattern: the
 * backend (GET /audits/:id/workflow) computes which move is legal RIGHT NOW
 * and why the rest is blocked, and this component renders that honestly —
 * a prominent forward button when the move is allowed, a disabled button
 * whose tooltip lists the blockers when it is not, amber advisories for
 * non-blocking hints, and a subtle "back" that requires a written reason
 * (backward moves land on the hash-chained audit trail with the reason).
 *
 * The final move (to "Wrapped up") is intentionally different: closing has
 * side effects owned by POST /audits/:id/close, so when the next stage is
 * `closed` the button hands off to the caller's wrap-up flow instead of
 * posting a bare stage change (which the backend rejects with a 409).
 */
import React, { useState } from 'react';

const API = process.env.REACT_APP_API_URL;

/** One plain sentence telling the user what to do right now. */
export function whatsNext(workflow, counts, audit) {
  if (!workflow) return '';
  switch (workflow.stage) {
    case 'setup':
      return audit?.engagement_letter
        ? 'The plan is set — move to Kickoff when you are ready.'
        : 'Set the scope and audit program, then generate the engagement notice — that opens Kickoff.';
    case 'engagement':
      if (audit?.engagement_letter_sent_at) return 'The engagement notice went out — move on when the auditee is engaged.';
      return audit?.engagement_letter
        ? 'Send the engagement notice to the auditee — email it from here or mark it sent.'
        : 'Generate the engagement notice, review it, then send it to the auditee.';
    case 'data_integrity': {
      const st = audit?.data_integrity_status;
      return (!st || st === 'pending')
        ? 'Check the auditee’s universe file, then record your decision: accept it or send it back.'
        : 'The data check is decided — move to Fieldwork to start testing.';
    }
    case 'fieldwork':
      if (audit && (audit.status === 'reviewing' || audit.status === 'scoring')) return 'The grounded review is running — results land here when it finishes.';
      if (audit?.status === 'completed') return 'Fieldwork is scored — review the results, then move to Report & findings.';
      if (!counts?.documents) return 'Upload the evidence and run the grounded review — or move on if fieldwork happens outside the platform.';
      return 'Evidence is in. Run the grounded review, or move to Report & findings if fieldwork was performed outside the platform.';
    case 'reporting': {
      const n = counts?.unraisedDeficient || 0;
      if (n > 0) return `${n} deficient result${n === 1 ? ' has' : 's have'} not been raised as findings — raise them, then wrap up the audit.`;
      return 'Assemble the report, raise any findings, and wrap up the audit when the record is complete.';
    }
    case 'closed':
      return 'This audit is wrapped up — the record is final.';
    default:
      return '';
  }
}

export default function WorkflowBar({ auditId, workflow, counts, audit, onChanged, onWrapUp }) {
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');

  if (!workflow) return null;
  const next = workflow.next;
  const nextIsClose = next && next.to === 'closed';

  const move = async (to, reason) => {
    setMoving(true); setError('');
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/stage`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason ? { to, reason } : { to }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.blockers?.length ? d.blockers.join(' ') : (d.message || 'That move is not allowed yet.'));
      if (onChanged) await onChanged();
    } catch (e) { setError(e.message); }
    setMoving(false);
  };

  const forward = () => {
    if (!next || !next.allowed || moving) return;
    if (nextIsClose) { if (onWrapUp) onWrapUp(); return; }
    move(next.to);
  };

  const back = () => {
    if (!workflow.back || moving) return;
    const reason = window.prompt(`Why move this audit back to ${workflow.back.label}? A one-line reason is required — it goes on the audit trail.`);
    if (reason == null) return; // cancelled
    if (!reason.trim()) { setError('A reason is required to move the audit back a step.'); return; }
    move(workflow.back.to, reason.trim());
  };

  const sentAt = audit?.engagement_letter_sent_at;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Audit lifecycle</div>
        {sentAt && (
          <div className="text-[11px] text-emerald-700 flex items-center gap-1">
            <span aria-hidden="true">✓</span>
            Sent to {audit.engagement_letter_sent_to || 'the auditee'} on {new Date(sentAt).toLocaleDateString()} {audit.engagement_letter_sent_via === 'email' ? 'via email' : 'manually'}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 overflow-x-auto pb-1 flex-1 min-w-0">
          {workflow.stages.map((s, i) => (
            <React.Fragment key={s.id}>
              <span className={`flex items-center gap-1 flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                s.state === 'done' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : s.state === 'current' ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow'
                : 'bg-slate-50 text-slate-400 border border-slate-200'
              }`}>
                {s.state === 'done' && <span aria-hidden="true">✓</span>}{s.label}
              </span>
              {i < workflow.stages.length - 1 && <span className={`w-3 h-px flex-shrink-0 ${s.state === 'done' ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {next && (
          <div className="relative group flex-shrink-0">
            <button
              type="button"
              onClick={forward}
              disabled={!next.allowed || moving}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                next.allowed && !moving
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow hover:shadow-md'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {moving ? 'Moving…' : nextIsClose ? 'Wrap up this audit' : `Move to ${next.label} →`}
            </button>
            {!next.allowed && next.blockers?.length > 0 && (
              <div className="absolute right-0 top-full mt-2 w-72 px-3 py-2 bg-slate-900 text-white text-xs rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none z-20 transition-opacity space-y-1">
                {next.blockers.map((b, i) => <div key={i}>{b}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      {next?.hints?.length > 0 && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
          {next.hints.join(' ')}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-slate-500">{whatsNext(workflow, counts, audit)}</div>
        {workflow.back && (
          <button type="button" onClick={back} disabled={moving} className="text-[11px] text-slate-400 hover:text-slate-600 flex-shrink-0">
            ← Back to {workflow.back.label}
          </button>
        )}
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
    </div>
  );
}
